import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Settings, Users, Percent, ShieldCheck } from "lucide-react";

const DEFAULT_SETTINGS = {
  reserve_pct: 40,
  iss_pct: 2.79,
  simples_pct: 7.7,
  tiers: [
    { min: 0, max: 5000, label: "Até R$ 5.000" },
    { min: 5000.01, max: 10000, label: "R$ 5.000 a R$ 10.000" },
    { min: 10000.01, max: 20000, label: "R$ 10.000 a R$ 20.000" },
  ],
  socios: [
    { id: "socio1", name: "Sócio 1", pct_t1: 34, pct_t2: 34, pct_t3: 34 },
    { id: "socio2", name: "Sócio 2", pct_t1: 33, pct_t2: 33, pct_t3: 33 },
    { id: "socio3", name: "Sócio 3", pct_t1: 33, pct_t2: 33, pct_t3: 33 },
  ],
};

const Num = ({ value, onChange, suffix = "%", step = "0.01", testid }) => (
  <div className="relative">
    <Input
      type="number"
      step={step}
      min={0}
      max={100}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="pr-8 text-right tabular-nums h-9"
      data-testid={testid}
    />
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{suffix}</span>
  </div>
);

export default function FinanceSettings() {
  const [s, setS] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/finance/settings");
        setS({ ...DEFAULT_SETTINGS, ...r.data });
      } catch {
        /* fallback to defaults */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const updSocio = (idx, key, val) => {
    setS((prev) => ({
      ...prev,
      socios: prev.socios.map((x, i) => (i === idx ? { ...x, [key]: val } : x)),
    }));
  };

  const sumTier = (k) =>
    s.socios.reduce((acc, x) => acc + Number(x[k] || 0), 0);

  const save = async () => {
    for (const k of ["pct_t1", "pct_t2", "pct_t3"]) {
      const total = sumTier(k);
      if (Math.abs(total - 100) > 0.01) {
        toast.error(`Soma dos % na faixa ${k.slice(-1)} deve ser 100 (atual: ${total.toFixed(2)})`);
        return;
      }
    }
    setSaving(true);
    try {
      await api.put("/finance/settings", {
        reserve_pct: Number(s.reserve_pct),
        iss_pct: Number(s.iss_pct),
        simples_pct: Number(s.simples_pct),
        invest_pct: Number(s.invest_pct || 0),
        tiers: s.tiers,
        socios: s.socios.map((x) => ({
          id: x.id,
          name: x.name,
          phone: (x.phone || "").trim(),
          pct_t1: Number(x.pct_t1),
          pct_t2: Number(x.pct_t2),
          pct_t3: Number(x.pct_t3),
        })),
      });
      toast.success("Configurações financeiras salvas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <div className="flex items-center justify-center py-16 text-slate-500">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Carregando…
      </div>
    );

  return (
    <div className="space-y-6 max-w-4xl pb-40 sm:pb-10" data-testid="finance-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center">
          <Settings className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl sm:text-2xl font-black text-slate-900">
            Configurações financeiras
          </h1>
          <p className="text-xs sm:text-sm text-slate-500">
            Reserva do caixa, impostos e distribuição dos sócios por faixa de valor.
          </p>
        </div>
      </div>

      {/* Taxas gerais */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 card-shadow">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-slate-700" />
          <h2 className="font-heading text-base font-black text-slate-900">Taxas e reservas</h2>
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-[12px] font-semibold">Reserva caixa</Label>
            <Num value={s.reserve_pct} onChange={(v) => setS({ ...s, reserve_pct: v })} testid="field-reserve" />
          </div>
          <div className="space-y-1">
            <Label className="text-[12px] font-semibold">ISS padrão</Label>
            <Num value={s.iss_pct} onChange={(v) => setS({ ...s, iss_pct: v })} testid="field-iss" />
          </div>
          <div className="space-y-1">
            <Label className="text-[12px] font-semibold">Simples Nacional</Label>
            <Num value={s.simples_pct} onChange={(v) => setS({ ...s, simples_pct: v })} testid="field-simples" />
          </div>
        </div>
        <div className="space-y-1 rounded-xl bg-violet-50 border border-violet-200 p-3">
          <Label className="text-[12px] font-semibold text-violet-900">Reserva de investimento (Rafael)</Label>
          <Num value={s.invest_pct ?? 0} onChange={(v) => setS({ ...s, invest_pct: v })} testid="field-invest" />
          <p className="text-[11px] text-violet-800">% separado do valor que vai para o Caixa em cada nota. Não mexe no saldo até você dar baixa na tela do Caixa. Vale só para as próximas notas.</p>
        </div>
        <p className="text-[11px] text-slate-500">
          ISS/Simples são usados como fallback quando o orçamento não tem valores próprios.
        </p>
      </section>

      {/* Faixas */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 card-shadow">
        <div className="flex items-start gap-2">
          <ShieldCheck className="w-4 h-4 text-slate-700 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-heading text-base font-black text-slate-900">
              Faixas de valor do orçamento
            </h2>
            <p className="text-[11.5px] text-slate-500 leading-snug mt-1">
              Cada orçamento é classificado em uma destas 3 faixas conforme o valor bruto da NF.
              Isso define qual coluna de % dos sócios é aplicada no cálculo (ver seção abaixo).
            </p>
          </div>
        </div>
        <div className="grid gap-2">
          {s.tiers.map((t, i) => (
            <div key={i} className="flex items-center gap-2 bg-slate-50/60 rounded-xl p-2 border border-slate-100">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center text-xs font-black shrink-0">
                F{i + 1}
              </div>
              <Input
                className="h-9 text-[13px] flex-1"
                value={t.label}
                placeholder="Nome da faixa"
                onChange={(e) =>
                  setS({
                    ...s,
                    tiers: s.tiers.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                  })
                }
              />
              <div className="hidden sm:flex items-center gap-1">
                <Input
                  className="h-9 w-24 text-right tabular-nums"
                  type="number"
                  value={t.min}
                  onChange={(e) =>
                    setS({
                      ...s,
                      tiers: s.tiers.map((x, j) => (j === i ? { ...x, min: Number(e.target.value) } : x)),
                    })
                  }
                />
                <span className="text-xs text-slate-400">até</span>
                <Input
                  className="h-9 w-24 text-right tabular-nums"
                  type="number"
                  value={t.max}
                  onChange={(e) =>
                    setS({
                      ...s,
                      tiers: s.tiers.map((x, j) => (j === i ? { ...x, max: Number(e.target.value) } : x)),
                    })
                  }
                />
              </div>
              <div className="flex items-center gap-1 shrink-0" title="% do recebido que vai para o Caixa nesta faixa">
                <span className="text-[10px] font-bold text-sky-700">Caixa</span>
                <Input
                  className="h-9 w-16 text-right tabular-nums border-sky-200"
                  type="number"
                  min="0"
                  max="100"
                  placeholder={String(s.reserve_pct)}
                  value={t.reserve_pct ?? ""}
                  data-testid={`field-tier-reserve-${i + 1}`}
                  onChange={(e) =>
                    setS({
                      ...s,
                      tiers: s.tiers.map((x, j) => (j === i ? { ...x, reserve_pct: e.target.value === "" ? null : Number(e.target.value) } : x)),
                    })
                  }
                />
                <span className="text-xs text-slate-400">%</span>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          <b>% do Caixa por faixa:</b> quanto do recebido (nota − ISS) vai para o Caixa em cada faixa. Em branco usa a “Reserva caixa” geral ({s.reserve_pct}%).
          Vale só para as próximas notas — as já apuradas mantêm o % que tinham.
        </p>
      </section>

      {/* Sócios */}
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4 card-shadow">
        <div className="flex items-start gap-2">
          <Users className="w-4 h-4 text-slate-700 mt-0.5" />
          <div className="flex-1">
            <h2 className="font-heading text-base font-black text-slate-900">Sócios e percentuais</h2>
            <p className="text-[11.5px] text-slate-500 leading-snug mt-1">
              Para cada sócio, defina quanto ele recebe do lucro em cada <strong>faixa de valor</strong> do orçamento.
              Ex.: um orçamento de R$ 3.000 cai na <strong>{s.tiers[0]?.label}</strong>, então o sistema usa a coluna <strong>F1</strong>.
              A soma dos 3 sócios em cada faixa precisa dar <strong>100%</strong>.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          {s.socios.map((x, i) => (
            <div key={x.id} className="rounded-xl border border-slate-200 bg-slate-50/50 p-3" data-testid={`socio-card-form-${i}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-slate-900 text-white font-black text-sm flex items-center justify-center shrink-0">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Nome do sócio {i + 1}
                  </Label>
                  <Input
                    className="h-9 text-[13px] mt-0.5"
                    value={x.name}
                    onChange={(e) => updSocio(i, "name", e.target.value)}
                    data-testid={`socio-name-${i}`}
                  />
                </div>
              </div>

              <div className="mb-3">
                <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                  Celular (envio de relatório)
                </Label>
                <Input
                  type="tel"
                  inputMode="tel"
                  placeholder="(11) 91234-5678"
                  className="h-9 text-[13px] mt-0.5"
                  value={x.phone || ""}
                  onChange={(e) => updSocio(i, "phone", e.target.value)}
                  data-testid={`socio-phone-${i}`}
                />
              </div>

              <div className="grid grid-cols-3 gap-2">
                {[1, 2, 3].map((n) => {
                  const tier = s.tiers[n - 1] || {};
                  const key = `pct_t${n}`;
                  return (
                    <div key={n}>
                      <Label className="text-[10px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">
                        <span className="inline-flex items-center justify-center w-4 h-4 rounded bg-emerald-600 text-white text-[9px]">
                          F{n}
                        </span>
                        Faixa {n}
                      </Label>
                      <p className="text-[9.5px] text-slate-500 mt-0.5 h-7 leading-tight">
                        {tier.label || `Faixa ${n}`}
                      </p>
                      <Num value={x[key]} onChange={(v) => updSocio(i, key, v)} testid={`socio-${i}-t${n}`} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Validation summary */}
          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-200">
            {[1, 2, 3].map((n) => {
              const sum = sumTier(`pct_t${n}`);
              const ok = Math.abs(sum - 100) < 0.01;
              const tier = s.tiers[n - 1] || {};
              return (
                <div
                  key={n}
                  className={`rounded-lg px-2 py-2 text-center ${ok ? "bg-emerald-50 border border-emerald-200" : "bg-rose-50 border border-rose-200"}`}
                  data-testid={`sum-tier-${n}`}
                >
                  <p className="text-[9.5px] font-black uppercase tracking-widest">
                    <span className={ok ? "text-emerald-700" : "text-rose-700"}>
                      Faixa {n}
                    </span>
                  </p>
                  <p className={`text-sm font-black tabular-nums ${ok ? "text-emerald-700" : "text-rose-700"}`}>
                    {sum.toFixed(2)}% {ok ? "✓" : "✗"}
                  </p>
                  <p className="text-[9px] text-slate-500 leading-tight mt-0.5 truncate">{tier.label}</p>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 leading-snug text-center">
            A soma dos % dos 3 sócios em cada faixa precisa ser <strong>100%</strong>. O sistema não deixa salvar se não bater.
          </p>
        </div>
      </section>

      <div className="fixed bottom-16 sm:bottom-0 inset-x-0 sm:static sm:inset-auto z-50 bg-white sm:bg-transparent border-t sm:border-0 border-slate-200 p-3 sm:p-0 shadow-[0_-6px_16px_rgba(15,23,42,0.08)] sm:shadow-none">
        <Button
          onClick={save}
          disabled={saving}
          data-testid="btn-save-finance-settings"
          className="w-full sm:w-auto h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-xl font-black px-6"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvando…
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" /> Salvar configurações
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
